import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PiggyBank, ExternalLink } from "lucide-react";
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { endringsLinjer } from "@/components/endringMerke";
import { formatNumber, formatCurrency, dec, num } from "@/lib/formatters";
import { tooltipStyle, gruppeInfo } from "./oversiktConstants";

const mill = (v) => (v == null ? "—" : `${formatNumber(Math.round(v / 1000))} mill. kr`);
const pct = (v) => (v == null ? "—" : `${dec(v, 1)} %`);

export const DisposisjonsfondChart = ({ data: payload, kommuneName }) => {
  const rows = payload?.rows?.filter((r) => r.pct != null);
  if (!rows?.length) return null;
  const g = gruppeInfo(payload);
  const fylkeKey = g.key;
  const utanFylke = g.utan;
  const first = rows[0];
  const last = rows[rows.length - 1];
  const diff = last.pct - first.pct;
  const chart = payload.rows.map((r) => ({
    year: r.year,
    Kommunen: r.pct,
    ...(utanFylke ? {} : { [fylkeKey]: g.pct(r) }),
    Landssnitt: r.land_pct,
    _kr: r.disposisjonsfond,
  }));
  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg mb-8" data-testid="disposisjonsfond-card">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <PiggyBank className="w-5 h-5 text-[#103766]" />
          Disposisjonsfond i prosent av driftsinntektene
        </CardTitle>
        <p className="text-sm text-[#4B647F]">Disposisjonsfondet er kommunens frie sparepengar – buffer mot svikt i inntekter eller uføresette utgifter. Eit fond på under 5 % av driftsinntektene blir ofte rekna som lågt.</p>
      </CardHeader>
      <CardContent className="pt-6 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="disposisjonsfond-pct">
            <p className="text-xs text-[#8AA4BF]">Andel av driftsinntektene {last.year}</p>
            <p className={`text-2xl font-light ${last.pct >= 5 ? "text-[#1F2A36]" : "text-[#C0392B]"}`}>{pct(last.pct)}</p>
            <p className="text-[11px] text-[#4B647F]">{first.year}: {pct(first.pct)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="disposisjonsfond-kr">
            <p className="text-xs text-[#8AA4BF]">Disposisjonsfond {last.year}</p>
            <p className="text-2xl font-light text-[#1F2A36]">{mill(last.disposisjonsfond)}</p>
            <p className="text-[11px] text-[#4B647F]">{formatCurrency(last.disposisjonsfond)}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="disposisjonsfond-snitt">
            <p className="text-xs text-[#8AA4BF]">{utanFylke ? "Landssnitt" : `${g.kort} og land`} {last.year}</p>
            <p className="text-2xl font-light text-[#3F8A86]">{pct(utanFylke ? last.land_pct : g.pct(last))}</p>
            <p className="text-[11px] text-[#4B647F]">{utanFylke ? "Oslo er både kommune og fylke" : `Landet: ${pct(last.land_pct)}`}</p>
          </div>
          <div className="p-3 border border-[#E6E9F0] rounded-lg" data-testid="disposisjonsfond-endring">
            <p className="text-xs text-[#8AA4BF]">Endring {first.year}–{last.year}</p>
            <p className={`text-2xl font-light ${diff >= 0 ? "text-[#2F7D5B]" : "text-[#C0392B]"}`}>{diff >= 0 ? "+" : "−"}{dec(Math.abs(diff), 1)} pp</p>
            <p className="text-[11px] text-[#4B647F]">prosentpoeng</p>
          </div>
        </div>
        <div className="h-[320px]" data-testid="disposisjonsfond-chart">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart} barCategoryGap="35%">
              {endringsLinjer(chart)}
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="year" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} width={55} tickFormatter={(v) => `${num(v)} %`} />
              <Tooltip
                formatter={(v, name, p) => (name === "Kommunen" && p.payload._kr ? `${pct(v)} (${mill(p.payload._kr)})` : pct(v))}
                contentStyle={tooltipStyle}
              />
              <Legend itemSorter={null} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="Kommunen" fill="#103766" radius={[4, 4, 0, 0]} />
              {!utanFylke && <Line type="monotone" dataKey={fylkeKey} stroke="#3F8A86" strokeWidth={2.5} dot={{ r: 3 }} connectNulls />}
              <Line type="monotone" dataKey="Landssnitt" stroke="#9CA3AF" strokeWidth={2} dot={{ r: 2 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <p className="text-xs text-[#8AA4BF]">
          {utanFylke
            ? `Landssnittet er eit uvekta gjennomsnitt av dei ${last.land_count} kommunane i landet i ${last.year}. Oslo er både kommune og fylke, så vi viser ikkje noko fylkessnitt her – det ville vore Oslo sine eigne tal om igjen.`
            : `Snitta er uvekta gjennomsnitt av kommunane sine andelar (${g.count(last)} ${g.count(last) === 1 ? "kommune" : "kommunar"} i ${g.ord}, ${last.land_count} i landet i ${last.year}). Færre kommunar i tidlege år skuldast kommune- og fylkessamanslåingar.`}
        </p>
        <p className="text-xs text-[#8AA4BF]">
          Kjelde: SSB <a href="https://www.ssb.no/statbank/table/13545" target="_blank" rel="noopener noreferrer" className="underline inline-flex items-center gap-1">13545 <ExternalLink className="w-3 h-3" /></a> – C.1.1 Disposisjonsfond, kommunekasse, målt mot brutto driftsinntekter.
        </p>
      </CardContent>
    </Card>
  );
};

export default DisposisjonsfondChart;
